// src/pages/admin/AdminCounties.js
// Manage the counties collection — this has to exist and be correct
// before towns can be added under it. County doc ID is a hand-picked
// slug that towns reference directly in their countyId field, so this
// page is the one place that slug gets created, avoiding typos that
// would otherwise silently break the county picker for a whole county.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCounties, createCounty, updateCounty, deleteCounty } from '../../lib/db';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const slugify = (name) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const AdminCounties = () => {
  const navigate = useNavigate();
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState('soon');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const c = await getCounties();
    setCounties(c);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startAdd = () => {
    setEditId(null);
    setName('');
    setSlug('');
    setStatus('soon');
    setAdding(true);
  };

  const startEdit = (county) => {
    setEditId(county.id);
    setName(county.name || '');
    setSlug(county.id);
    setStatus(county.status || 'soon');
    setAdding(true);
    window.scrollTo(0, 0);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('County name is required'); return; }
    const finalSlug = editId || slug.trim() || slugify(name);
    if (!finalSlug) { toast.error('Could not generate a valid ID from that name'); return; }

    // Prevent accidentally creating a second county with the same slug
    if (!editId && counties.some(c => c.id === finalSlug)) {
      toast.error(`A county with ID "${finalSlug}" already exists`);
      return;
    }

    setSaving(true);
    try {
      if (editId) {
        await updateCounty(editId, { name: name.trim(), status });
        toast.success(`${name} updated`);
      } else {
        await createCounty(finalSlug, { name: name.trim(), status });
        toast.success(`${name} added — now add towns under it`);
      }
      setAdding(false);
      setEditId(null);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save county');
    }
    setSaving(false);
  };

  const handleDelete = async (id, countyName) => {
    if (!window.confirm(`Delete "${countyName}"? Any towns already using this county ID will stop appearing in the county picker. This cannot be undone.`)) return;
    await deleteCounty(id);
    toast.success('County deleted');
    load();
  };

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
      <button onClick={() => navigate('/admin')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 }}>
        <div style={{ width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' }}>←</div>
        <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>Admin</span>
      </button>

      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>🗺️ Counties</div>
      <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginBottom: 16 }}>
        Add a county here first — then add towns under it in the Towns screen.
      </div>

      <button onClick={() => adding ? setAdding(false) : startAdd()}
        style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 15, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', marginBottom: 16 }}>
        {adding ? '✕ Cancel' : '+ Add county'}
      </button>

      {adding && (
        <div style={{ background: 'var(--white)', borderRadius: 18, padding: 18, marginBottom: 16, boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)' }}>{editId ? 'Edit county' : 'New county'}</div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 5 }}>County name</div>
            <input className="form-input" value={name}
              onChange={e => {
                setName(e.target.value);
                if (!editId) setSlug(slugify(e.target.value));
              }}
              placeholder="e.g. Clare" />
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 5 }}>
              ID {editId && '(can\'t be changed once created)'}
            </div>
            <input className="form-input" value={slug}
              disabled={!!editId}
              onChange={e => setSlug(slugify(e.target.value))}
              placeholder="e.g. clare"
              style={editId ? { opacity: 0.6 } : undefined} />
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>
              This exact ID is what towns will use in their "County ID" field — auto-filled from the name, but you can adjust it before saving.
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 5 }}>Status</div>
            <select className="form-input" value={status} onChange={e => setStatus(e.target.value)}>
              <option value="soon">Coming soon</option>
              <option value="live">Live</option>
            </select>
          </div>

          <button onClick={handleSave} disabled={saving}
            style={{ background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 15, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving...' : editId ? 'Save changes' : 'Add county'}
          </button>
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 10 }}>
        All counties ({counties.length})
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>
      ) : counties.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🗺️</div>
          <div className="empty-state-title">No counties yet</div>
          <div className="empty-state-body">Add one above — you'll need it before towns in a new county can appear in the county picker.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {counties.map(county => (
            <div key={county.id} style={{ background: 'var(--white)', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{county.name}</div>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 100, fontWeight: 700, flexShrink: 0, background: county.status === 'live' ? 'var(--sage-pale)' : '#F0F0EE', color: county.status === 'live' ? 'var(--sage-deep)' : 'var(--muted)' }}>
                  {county.status || 'soon'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 4 }}>ID: {county.id}</div>
              {county.towns && <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 4 }}>Towns: {county.towns}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={() => startEdit(county)}
                  style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                  Edit
                </button>
                <button onClick={() => handleDelete(county.id, county.name)}
                  style={{ background: 'var(--error-pale)', color: 'var(--error)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

export default AdminCounties;
